'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import { cn } from '@/shared/lib/cn';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { Button } from '@/shared/ui/shadcn/button';
import { useTokenBalance } from '@/shared/api/hooks/use-token-balance';
import { useTokenNudge } from '@/shared/api/hooks/use-user';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { apiClient } from '@/shared/lib/api';
import type { PaywallAction } from '@/shared/lib/api';
import { assistantTargets, elementTarget } from '@/shared/assistant';

/**
 * Nav low-balance nudge pill.
 *
 * Server-driven: the popover appears ONLY while `user.tokens.nudge` is
 * non-null — the API owns the "running low" band and threshold (previously a
 * hardcoded `balance < 300` client heuristic, removed). Title/body/CTA copy
 * all come from the server (already localized); the client renders verbatim
 * and only resolves the action `kind` to a route. The amber/destructive tint
 * on the figure is a thin presentation cue, not a band decision.
 */
export function TokenBalanceIndicator() {
  const tCommon = useTranslations('common');
  const tokenBalance = useTokenBalance();
  const nudge = useTokenNudge();
  const showPaywall = useLimitPaywallStore((s) => s.showPaywall);

  // Guard so the display-ack fires at most once per open transition (not on
  // every Radix open/close toggle). The popover body is what counts as the
  // nudge actually being shown, so we ack when it opens.
  const ackedRef = React.useRef(false);

  // No server nudge → no pill. Band ownership is fully server-side.
  if (!nudge) {
    return null;
  }

  const handleOpenChange = (open: boolean) => {
    if (open && !ackedRef.current) {
      ackedRef.current = true;
      // Confirm display so the server arms its once-per-band dedupe.
      // Fire-and-forget — swallowed inside the client, never blocks UI.
      void apiClient.acknowledgeTokenNudge();
    }
  };

  const runAction = (action: PaywallAction) => {
    switch (action.kind) {
      case 'purchase_tokens':
        // Open the token sheet in place instead of navigating to the account
        // storefront — same destination as the render-surface nudge and as
        // Apple/Android, so the CTA means one thing everywhere.
        showPaywall(nudge, 'LOW_TOKENS_NUDGE');
        break;
      // dismiss / delete_existing: popover closes on its own (Radix), nothing
      // else to route for the nudge surface.
      default:
        break;
    }
  };

  const roundedBalance = Math.max(0, Math.round(tokenBalance));

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
        data-assistant-target={assistantTargets(elementTarget('token_balance'))}
          className={cn(
            'flex items-center gap-1.5 px-2.5 h-8 rounded-full transition-all duration-300',
            'bg-amber-500/10 hover:bg-amber-500/20',
            'border border-amber-500/20'
          )}
          title={tCommon('tokenBalance')}
        >
          <WalnutIcon
            className={cn(
              'h-3.5 w-[11px] md:block hidden',
              roundedBalance <= 50 ? 'text-destructive' : 'text-amber-500'
            )}
          />
          <span
            className={cn(
              'text-list-item-value',
              roundedBalance <= 50
                ? 'text-destructive'
                : 'text-amber-600 dark:text-amber-400'
            )}
          >
            {roundedBalance}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <WalnutIcon className="h-4 w-[13px] text-amber-500" />
            {/* Server-driven, already-localized copy — rendered verbatim. */}
            <h4 className="font-medium">{nudge.title}</h4>
          </div>

          <p className="text-form-help text-muted-foreground">{nudge.body}</p>

          <div className="flex gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={() => runAction(nudge.dismiss_action)}
            >
              {nudge.dismiss_action.label}
            </Button>
            {nudge.primary_action && (
              <Button
                size="sm"
                className="flex-1"
                onClick={() => runAction(nudge.primary_action!)}
              >
                {nudge.primary_action.label}
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
